import { CloseButton } from "@components/buttons/close";
import {
  SHButton,
  SHDialog,
  SHStack,
  SHTypography,
} from "@components/design-systems";
import { CheckSVG } from "@components/svgs";
import { ModelScreenerColumnSettingDTO } from "@models/model-screener/entities/modelScreenerFilter";
import CloudDoneOutlinedIcon from "@mui/icons-material/CloudDoneOutlined";
import { DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { MSTableColumnSettingSortableContext } from "@pages/managed-accounts/model-screener/components/model-screener-table/toolbar/column-setting/sortable-context";
import { MSTableResetButton } from "@pages/managed-accounts/model-screener/components/model-screener-table/toolbar/reset-button";
import { RootState, useAppDispatch } from "@redux/store";
import { generateUUID } from "@utils";
import { useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { MSColumnDropdownButton } from "../dropdown-button";
import { MSColumnSaveModal } from "./save-modal";
import { useToggle } from "react-use";
import { MSTableColumnSettingModalContentSkeleton } from "../skeleton/modal-content";
import {
  setColumnSetting,
  setColumnSettings,
  updateModelScreenerColumnSetting,
} from "@redux/slices/model-screener";
import { useSelector } from "react-redux";
import { MSFilterDefaultValueConstants } from "../../filter/constants";
import { useModelScreener } from "@hooks/useModelScreener";
import { cloneDeep } from "lodash";
import { MSColumnSettingsDefaultValueConstants } from "../constants";

type MSTableColumnSettingModalProps = {
  msColumnSettings: ModelScreenerColumnSettingDTO;
  open: boolean;
  onClose: () => void;
};

export const MSTableColumnSettingModal = ({
  open,
  msColumnSettings,
  onClose,
}: MSTableColumnSettingModalProps) => {
  const dispatch = useAppDispatch();

  // Refs
  const abortController = useRef<AbortController | null>(null);

  // Store
  const defaultColumnSettingData = useSelector(
    (state: RootState) => state.modelScreener.internal.defaultColumnSettingData,
  );
  const isLoadingModelScreenerColumnSetting = useSelector(
    (state: RootState) => {
      return state.modelScreener.ui.isLoadingModelScreenerColumnSetting;
    },
  );
  const modelScreenerColumnSettings = useSelector((state: RootState) => {
    return state.modelScreener.columnSettings;
  });

  const { loadMSColumnSettingById, setMSColumnSettingAsDefault } =
    useModelScreener();

  // Forms
  const { getValues, reset } = useFormContext<ModelScreenerColumnSettingDTO>();

  const {
    createMSColumnSetting,
    updateMSColumnSetting,
    deleteModelScreenerColumnSetting,
  } = useModelScreener();

  // States
  const [disabled, setDisabled] = useToggle(false);
  const [isResetting, setIsResetting] = useToggle(false);
  const [isReloading, toggleIsReloading] = useToggle(false);
  const [key, setKey] = useState(generateUUID());
  const [saveModalOpening, toggleSaveModal] = useToggle(false);

  // Side effects
  useEffect(() => {
    if (!open) {
      abortController.current?.abort();
      return;
    }
    toggleIsReloading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Functions
  const onModalClose = () => {
    onClose();
    reset(msColumnSettings);
    setKey(generateUUID());
  };

  const handleOnApplySettings = () => {
    const currentFormData = getValues();
    onApply(cloneDeep(currentFormData));
    onClose();
    setDisabled(false);
  };

  const handleOnSelectColumnSetting = async (
    selectedColumnSetting: ModelScreenerColumnSettingDTO,
  ): Promise<boolean> => {
    abortController.current = new AbortController();
    try {
      const response = await loadMSColumnSettingById({
        selectedColumnSettingId: selectedColumnSetting?.id,
        signal: abortController.current?.signal,
      });
      if (!response) return false;
      setKey(generateUUID());
      onApply(response);
      return true;
    } finally {
      setTimeout(
        () => toggleIsReloading(false),
        MSFilterDefaultValueConstants.StandardDelayTimeMs,
      );
    }
  };

  const onApply = (formData: ModelScreenerColumnSettingDTO) => {
    dispatch(updateModelScreenerColumnSetting({ columnSetting: formData }));
  };

  const handleOnReset = () => {
    if (!defaultColumnSettingData) return;
    reset(defaultColumnSettingData);
    setKey(generateUUID());
    setDisabled(false);
    setIsResetting(false);
  };

  const handleOnSaveButtonClick = () => {
    toggleSaveModal(true);
  };

  const handleOnSaveColumn = async ({
    viewName,
    isUserDefault,
    overwriteId,
  }: {
    viewName: string;
    isUserDefault: boolean;
    overwriteId?: string;
  }): Promise<boolean> => {
    abortController.current = new AbortController();
    const { data } = getValues();
    const modelScreenerColumn = {
      id: overwriteId,
      name: viewName,
      isUserDefault: isUserDefault,
      data: data,
    } as ModelScreenerColumnSettingDTO;

    if (overwriteId) {
      const updatedColumnList = await updateMSColumnSetting(
        modelScreenerColumn,
        abortController.current.signal,
      );
      if (!updatedColumnList) return false;
      dispatch(
        setColumnSettings({
          columnSettings: updatedColumnList,
        }),
      );
    } else {
      const columnList = await createMSColumnSetting(
        modelScreenerColumn,
        abortController.current.signal,
      );
      if (!columnList) return false;

      const selectedColumnId = columnList.find(
        (x) => x.name === modelScreenerColumn.name,
      )?.id;
      const newFilter = {
        ...modelScreenerColumn,
        id: selectedColumnId ?? "",
      };

      reset(newFilter);
      dispatch(
        setColumnSettings({
          columnSettings: columnList,
        }),
      );
    }

    return true;
  };

  const handleOnSetColumnSettingAsDefault = async (
    dto: ModelScreenerColumnSettingDTO,
  ): Promise<boolean> => {
    const currentFormData = getValues();
    const currentFilterId = currentFormData.id;

    if (
      currentFilterId === dto?.id &&
      dto?.id !== MSColumnSettingsDefaultValueConstants.DefaultColumnSettingId
    ) {
      const { id, name } = getValues();
      const saveResult = await handleOnSaveColumn({
        viewName: name,
        isUserDefault: true,
        overwriteId: id,
      });

      if (!saveResult) return false;
    }

    const setDefaultResult = await setMSColumnSettingAsDefault(dto?.id);

    if (!setDefaultResult) return false;

    if (currentFilterId === dto?.id) {
      dispatch(setColumnSetting({ columnSetting: currentFormData }));
    }

    return true;
  };

  const handleOnDeleteFilterSetting = async (
    deletedFilter: ModelScreenerColumnSettingDTO,
  ) => {
    await deleteModelScreenerColumnSetting(deletedFilter.id);
  };

  return (
    <SHDialog
      open={open}
      keepMounted={true}
      maxWidth={false}
      onClose={onModalClose}
      sx={{
        "& .MuiPaper-root": {
          minWidth: "800px",
          width: {
            xs: "90%",
            xl: "800px",
          },
          maxWidth: "50%",
          minHeight: "700px",
          padding: "0px 0px",
          position: "relative",
        },
      }}
    >
      {/* Dialog title */}
      <DialogTitle>
        <SHStack gap={2}>
          <SHStack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <SHTypography variant="h2" fontWeight={600}>
              Column settings
            </SHTypography>
          </SHStack>

          <SHStack
            direction={"row"}
            width={"100%"}
            height={"fit-content"}
            alignItems={"flex-start"}
            justifyContent={"space-between"}
          >
            <SHStack direction={"row"} gap={1.5}>
              <MSColumnDropdownButton
                buttonTitle={"Load view"}
                options={modelScreenerColumnSettings}
                onSelectOption={async (dto) => {
                  toggleIsReloading(true);
                  if (dto.isSystemDefault) {
                    setTimeout(() => {
                      if (defaultColumnSettingData !== null) {
                        onApply(defaultColumnSettingData);
                      }
                    }, 0);
                    setTimeout(() => {
                      toggleIsReloading(false);
                    }, MSFilterDefaultValueConstants.StandardDelayTimeMs);
                    return true;
                  } else {
                    return await handleOnSelectColumnSetting(dto);
                  }
                }}
                onDeleteOption={handleOnDeleteFilterSetting}
                onMakeDefaultOption={async (dto) => {
                  return await handleOnSetColumnSettingAsDefault(dto);
                }}
              />
              <SHStack direction={"row"}>
                <SHButton
                  key={`save-button-${isResetting}-${isReloading}-${isLoadingModelScreenerColumnSetting}`}
                  color="primary"
                  size="medium"
                  variant="outlined"
                  startIcon={
                    <CloudDoneOutlinedIcon sx={{ marginTop: "-2px" }} />
                  }
                  onClick={handleOnSaveButtonClick}
                  disabled={
                    disabled ||
                    isReloading ||
                    isLoadingModelScreenerColumnSetting
                  }
                >
                  Save current setting
                </SHButton>
                {saveModalOpening && (
                  <MSColumnSaveModal
                    open={saveModalOpening}
                    onClose={() => {
                      toggleSaveModal(false);
                    }}
                    onSaveColumn={handleOnSaveColumn}
                  />
                )}
              </SHStack>
            </SHStack>
            <SHStack>
              <SHStack direction={"row"} gap={1.5}>
                <MSTableResetButton
                  key={`reset-button-${isResetting}-${isReloading}-${isLoadingModelScreenerColumnSetting}`}
                  disabled={
                    disabled ||
                    isReloading ||
                    isLoadingModelScreenerColumnSetting
                  }
                  label={"Reset all"}
                  height={"32px"}
                  onClick={() => {
                    setDisabled(true);
                    setIsResetting(true);
                    setTimeout(() => {
                      handleOnReset();
                    }, 0);
                  }}
                />
                <SHButton
                  key={`apply-button-${isResetting}-${isReloading}-${isLoadingModelScreenerColumnSetting}`}
                  disabled={
                    disabled ||
                    isReloading ||
                    isLoadingModelScreenerColumnSetting
                  }
                  variant={"contained"}
                  startIcon={<CheckSVG width={18} stroke={"white"} />}
                  onClick={() => {
                    setDisabled(true);
                    setTimeout(() => handleOnApplySettings(), 0);
                  }}
                >
                  Apply settings
                </SHButton>
              </SHStack>
            </SHStack>
          </SHStack>
        </SHStack>
        <SHStack
          sx={{
            position: "absolute",
            top: "22px",
            right: "25px",
          }}
        >
          <CloseButton width="17px" height="17px" onClick={onModalClose} />
        </SHStack>
      </DialogTitle>
      {/* End dialog title */}

      {/* Dialog content */}
      <DialogContent>
        <SHStack display={isResetting || isReloading ? "block" : "none"}>
          <MSTableColumnSettingModalContentSkeleton />
        </SHStack>
        <SHStack display={!isResetting && !isReloading ? "block" : "none"}>
          <MSTableColumnSettingSortableContext
            key={`${key}-${open}`}
            disabled={disabled}
          />
        </SHStack>
      </DialogContent>
      {/* End dialog content */}

      {/* Dialog actions */}
      <DialogActions></DialogActions>
      {/* End dialog actions */}
    </SHDialog>
  );
};
